/*
Vehicle Audit History Component
Displays comprehensive audit trail for vehicle changes
Author: System
Date: 2025-01-27
*/

import React, { useState, useEffect } from 'react'
import { Card, Text, Flex, Badge, Button, ScrollArea, Separator, IconButton } from '@radix-ui/themes'
import { ClockIcon, UserIcon, ChevronDownIcon, ChevronUpIcon, ActivityIcon } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'

import { customFetch } from '@/utils/customFetch'
import { useAuth } from '@/hooks/useAuth'
import { UserProfileRole } from '@/modules/users/types/UserProfile'
import { useFormatter } from '@/utils/formats'

interface AuditLogEntry {
  _id: string
  entityType: 'vehicle'
  entityId: string
  action: 'create' | 'update' | 'delete' | 'archive' | 'restore' | 'publish' | 'unpublish'
  changes: any[]
  previousData?: any
  newData?: any
  userId: string
  userEmail: string
  organizationId: string
  timestamp: string
  changesSummary: string[]
  metadata?: {
    route?: string
    userAgent?: string
    ip?: string
    reason?: string
    platform?: 'yapo' | 'chileautos' | 'meli'
    discount?: {
      originalPrice: number
      discountedPrice: number
      discountAmount: number
      requiresApproval: boolean
      isPreApproved: boolean
      approvedBy?: string
    }
  }
}

interface VehicleAuditHistoryProps {
  vehicleId: string
  onClose?: () => void
}

const actionColors: Record<string, any> = {
  create: 'green',
  update: 'blue',
  delete: 'red',
  archive: 'orange',
  restore: 'green',
  publish: 'purple',
  unpublish: 'gray',
}

const actionLabels: Record<string, string> = {
  create: 'Creado',
  update: 'Modificado',
  delete: 'Eliminado',
  archive: 'Archivado',
  restore: 'Restaurado',
  publish: 'Publicado',
  unpublish: 'Despublicado',
}

const getEntryColor = (entry: AuditLogEntry) => {
  if (entry.metadata?.route === 'sale-note-creation') return 'purple'

  if (entry.metadata?.route === 'salenote-approve') return 'amber'

  if (entry.metadata?.route === 'salenote-reassign') return 'cyan'

  return actionColors[entry.action] || 'gray'
}

const getEntryLabel = (entry: AuditLogEntry) => {
  if (entry.metadata?.route === 'sale-note-creation') return 'Nota de Venta Creada'

  if (entry.metadata?.route === 'salenote-approve') return 'Vehículo Vendido (Nota Aprobada)'

  if (entry.metadata?.route === 'salenote-reassign') return 'Vendedor/Aprobador Corregido'

  return actionLabels[entry.action] || entry.action
}

export const VehicleAuditHistory: React.FC<VehicleAuditHistoryProps> = ({ vehicleId, onClose }) => {
  const [auditHistory, setAuditHistory] = useState<AuditLogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set())
  const { userData } = useAuth()
  const { formatCurrency } = useFormatter()

  useEffect(() => {
    fetchAuditHistory()
  }, [vehicleId])

  const fetchAuditHistory = async () => {
    try {
      setLoading(true)
      const response = await customFetch<{
        success: boolean
        history: AuditLogEntry[]
        count: number
      }>(`${import.meta.env.VITE_API_URL}vehicles/${vehicleId}/audit-history`)

      if (response.success) {
        setAuditHistory(response.history)
      } else {
        setError('Failed to load audit history')
      }
    } catch (err) {
      setError('Error fetching audit history')
      console.error('Audit history fetch error:', err)
    } finally {
      setLoading(false)
    }
  }

  const toggleExpanded = (entryId: string) => {
    const newExpanded = new Set(expandedEntries)

    if (newExpanded.has(entryId)) {
      newExpanded.delete(entryId)
    } else {
      newExpanded.add(entryId)
    }
    setExpandedEntries(newExpanded)
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)

    return formatDistanceToNow(date, {
      addSuffix: true,
      locale: es,
    })
  }

  const renderChangesSummary = (changes: string[]) => {
    if (!changes || changes.length === 0) return null

    return (
      <Flex direction="column" gap="1" mt="2">
        <Text size="2" weight="medium" color="gray">
          Cambios realizados:
        </Text>
        {changes.map((change, index) => (
          <Text key={index} size="1" color="gray">
            • {change}
          </Text>
        ))}
      </Flex>
    )
  }

  const renderMetadata = (metadata?: AuditLogEntry['metadata']) => {
    if (!metadata) return null

    return (
      <Flex direction="column" gap="1" mt="2" p="2" style={{ backgroundColor: 'var(--gray-2)', borderRadius: '4px' }}>
        <Text size="2" weight="medium" color="gray">
          Información adicional:
        </Text>

        {/* Discount Information */}
        {metadata.discount && (
          <Flex direction="column" gap="1" mt="1">
            <Flex align="center" gap="2">
              <Text size="2" weight="medium" color="orange">
                💰 Descuento Aplicado:
              </Text>
              <Badge color="orange" size="1">
                {formatCurrency(metadata.discount.discountAmount)}
              </Badge>
            </Flex>
            <Text size="1" color="gray">
              <strong>Precio:</strong> {formatCurrency(metadata.discount.originalPrice)} →{' '}
              {formatCurrency(metadata.discount.discountedPrice)}
            </Text>
            {metadata.discount.requiresApproval && !metadata.discount.isPreApproved && (
              <Flex align="center" gap="1">
                <Text size="1" color="orange">
                  ⚠️
                </Text>
                <Text size="1" color="orange">
                  Requiere aprobación
                </Text>
              </Flex>
            )}
            {metadata.discount.isPreApproved && (
              <Flex align="center" gap="1">
                <Text size="1" color="green">
                  ✅
                </Text>
                <Text size="1" color="green">
                  Pre-aprobado
                </Text>
              </Flex>
            )}
          </Flex>
        )}

        {metadata.reason && (
          <Text size="1" color="gray">
            <strong>Motivo:</strong> {metadata.reason}
          </Text>
        )}
        {metadata.platform && (
          <Text size="1" color="gray">
            <strong>Plataforma:</strong> {metadata.platform.toUpperCase()}
          </Text>
        )}
        {metadata.route && (
          <Text size="1" color="gray">
            <strong>Ruta:</strong> {metadata.route}
          </Text>
        )}
      </Flex>
    )
  }

  if (loading) {
    return (
      <Card>
        <Flex align="center" justify="center" p="6">
          <ActivityIcon className="animate-spin mr-2" />
          <Text>Cargando historial de auditoría...</Text>
        </Flex>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <Flex align="center" justify="center" p="6">
          <Text color="red">{error}</Text>
        </Flex>
      </Card>
    )
  }

  return (
    <Card>
      <Flex direction="column" gap="3">
        {/* Header */}
        <Flex justify="between" align="center">
          <Flex align="center" gap="2">
            <ClockIcon size={20} />
            <Text size="4" weight="bold">
              Historial de Auditoría
            </Text>
            <Badge color="gray" size="1">
              {auditHistory.length} entradas
            </Badge>
          </Flex>
          {onClose && (
            <Button variant="ghost" onClick={onClose}>
              Cerrar
            </Button>
          )}
        </Flex>

        <Separator />

        {/* Timeline */}
        <ScrollArea style={{ maxHeight: '600px' }}>
          <Flex direction="column" gap="3">
            {auditHistory.length === 0 ? (
              <Flex align="center" justify="center" p="6">
                <Text color="gray">No hay historial de auditoría disponible</Text>
              </Flex>
            ) : (
              auditHistory.map((entry, index) => {
                const isExpanded = expandedEntries.has(entry._id)
                const isLast = index === auditHistory.length - 1

                return (
                  <div key={entry._id} style={{ position: 'relative' }}>
                    {/* Timeline line */}
                    {!isLast && (
                      <div
                        style={{
                          position: 'absolute',
                          left: '16px',
                          top: '40px',
                          bottom: '-12px',
                          width: '2px',
                          backgroundColor: 'var(--gray-6)',
                        }}
                      />
                    )}

                    {/* Timeline dot */}
                    <div
                      style={{
                        position: 'absolute',
                        left: '12px',
                        top: '16px',
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: `var(--${getEntryColor(entry)}-9)`,
                      }}
                    />

                    {/* Content */}
                    <Card style={{ marginLeft: '32px' }}>
                      <Flex direction="column" gap="2">
                        {/* Main info */}
                        <Flex justify="between" align="start">
                          <Flex direction="column" gap="1">
                            <Flex align="center" gap="2">
                              <Badge color={getEntryColor(entry)} size="1">
                                {getEntryLabel(entry)}
                              </Badge>
                              {/* Discount indicator */}
                              {entry.metadata?.discount && (
                                <Badge color="orange" size="1">
                                  Descuento: {formatCurrency(entry.metadata.discount.discountAmount)}
                                </Badge>
                              )}
                              <Text size="2" color="gray">
                                {formatTimestamp(entry.timestamp)}
                              </Text>
                            </Flex>

                            <Flex align="center" gap="2">
                              <UserIcon size={14} />
                              <Text size="2">{entry.userEmail}</Text>
                            </Flex>
                          </Flex>

                          {(entry.changes.length > 0 || entry.metadata) && (
                            <IconButton variant="ghost" size="1" onClick={() => toggleExpanded(entry._id)}>
                              {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            </IconButton>
                          )}
                        </Flex>

                        {/* Changes summary (always visible) */}
                        {entry.changesSummary && entry.changesSummary.length > 0 && (
                          <div>
                            {renderChangesSummary(entry.changesSummary.slice(0, isExpanded ? undefined : 3))}
                            {!isExpanded && entry.changesSummary.length > 3 && (
                              <Text size="1" color="gray" mt="1">
                                ... y {entry.changesSummary.length - 3} cambios más
                              </Text>
                            )}
                          </div>
                        )}

                        {/* Expanded details */}
                        {isExpanded && (
                          <div>
                            {renderMetadata(entry.metadata)}

                            {/* Raw changes for debugging (only for admin users) */}
                            {userData?.profile?.roles?.includes(UserProfileRole.ViewProfiles) &&
                              entry.changes.length > 0 && (
                                <details style={{ marginTop: '8px' }}>
                                  <summary>
                                    <Text size="1" color="gray">
                                      Cambios técnicos ({entry.changes.length})
                                    </Text>
                                  </summary>
                                  <pre
                                    style={{
                                      fontSize: '11px',
                                      backgroundColor: 'var(--gray-2)',
                                      padding: '8px',
                                      borderRadius: '4px',
                                      overflow: 'auto',
                                      marginTop: '4px',
                                    }}
                                  >
                                    {JSON.stringify(entry.changes, null, 2)}
                                  </pre>
                                </details>
                              )}
                          </div>
                        )}
                      </Flex>
                    </Card>
                  </div>
                )
              })
            )}
          </Flex>
        </ScrollArea>

        {/* Footer */}
        <Separator />
        <Flex justify="between" align="center">
          <Text size="1" color="gray">
            Mostrando las últimas {auditHistory.length} entradas
          </Text>
          <Button variant="outline" size="1" onClick={fetchAuditHistory}>
            <ActivityIcon size={14} />
            Actualizar
          </Button>
        </Flex>
      </Flex>
    </Card>
  )
}

export default VehicleAuditHistory
